import { useMemo, useState } from 'react';
import { fromJS } from 'immutable';
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { useAuthInfoProvider } from 'content-management-data-lib';
import { CardWrapper, CardSection } from 'foundations-components/transitional/components/Card';
import { Select } from 'foundations-components/components/Select';
import { Select as TransitionalSelect } from 'foundations-components/transitional/components/Select';
import { View } from 'foundations-components/components/View';
import { InlineLabel } from 'foundations-components/transitional/components/InlineLabel';
import { FilterBar } from 'foundations-components/transitional/components/FilterBar';
import { colorAssignmentSets } from 'reporting-ui-components/constants/colorAssignmentSets';
import PlatformReportResolver from 'reporting-ui-components/components/PlatformReport/PlatformReportResolver';
import { AREA, LINE } from 'reporting-data/constants/chartTypes';
import { TIME_SERIES } from 'reporting-data/constants/configTypes';
import { ANALYTICS_ALL_PAGES, ANALYTICS_ALL_PAGES_SOURCES } from 'reporting-data/constants/dataTypes';
import { SUM } from 'reporting-data/constants/metricTypes';
import { EQ, IN } from 'reporting-data/constants/operators';
import { useReportingFiltersProvider } from '../../providers/ReportingFiltersProvider';
import { getChartStyleOptions, isSecondaryMetric } from '../../utils/reportingUtils';
import useReportingContentIds from '../../hooks/useReportingContentIds';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const getCustomFilters = ({
  contentIds,
  checkedSources,
  contentId,
  d1,
  source
}) => {
  if (contentIds && contentIds.length > 0) {
    return [{
      operator: IN,
      property: 'filters',
      values: contentIds
    }];
  }
  const defaultFilters = [{
    operator: EQ,
    property: 'pageId',
    value: contentId
  }];
  if (checkedSources.length === 0) {
    return defaultFilters;
  }
  if (!source) {
    defaultFilters.push({
      operator: EQ,
      property: 'd1',
      value: 'summary'
    }, {
      operator: IN,
      property: 'filters',
      values: checkedSources
    });
    return defaultFilters;
  }
  if (!d1) {
    defaultFilters.push({
      operator: EQ,
      property: 'd1',
      value: 'summary'
    }, {
      operator: EQ,
      property: 'filters',
      value: source
    });
    return defaultFilters;
  }
  defaultFilters.push({
    operator: EQ,
    property: 'd1',
    value: source
  }, {
    operator: EQ,
    property: 'filters',
    value: d1
  });
  return defaultFilters;
};
const getXAxisLabel = selectedMetric => {
  switch (selectedMetric) {
    case 'submissions':
      return I18n.text('content-management-details-lib.reporting.sourcesChart.xAxisLabel.submissionDate');
    case 'contacts':
    case 'contactToCustomerRate':
    case 'leads':
    case 'subscribers':
      return I18n.text('content-management-details-lib.reporting.sourcesChart.xAxisLabel.conversionDate');
    default:
      return I18n.text('content-management-details-lib.reporting.sourcesChart.xAxisLabel.viewDate');
  }
};
const getDimensions = ({
  checkedSources,
  isViewingAllVariations
}) => {
  if (isViewingAllVariations || checkedSources.length === 0) {
    return ['sessionDate'];
  }
  return ['sessionDate', 'breakdown'];
};
const getReport = ({
  contentIds,
  contentId,
  dateRange,
  d1,
  frequency,
  chartType,
  selectedMetric,
  source,
  checkedSources
}) => {
  const isViewingAllVariations = Boolean(contentIds && contentIds.length > 0);
  if (!(contentId || isViewingAllVariations)) {
    return null;
  }
  return fromJS({
    chartType,
    config: {
      configType: TIME_SERIES,
      dataType: isViewingAllVariations ? ANALYTICS_ALL_PAGES : ANALYTICS_ALL_PAGES_SOURCES,
      dimensions: getDimensions({
        isViewingAllVariations,
        checkedSources
      }),
      filters: {
        custom: getCustomFilters({
          contentIds,
          contentId,
          d1,
          source,
          checkedSources
        }),
        dateRange: {
          property: 'sessionDate',
          value: dateRange
        }
      },
      frequency,
      metrics: [{
        metricTypes: [SUM],
        property: selectedMetric
      }],
      sort: []
    },
    displayParams: {
      checked: checkedSources,
      colors: {
        custom: colorAssignmentSets.get('sources')
      },
      // rhumbConfig: getReportRhumbConfig('SOURCES_CHART_LOADED'),
      stacked: chartType !== LINE,
      xAxis: {
        title: {
          text: getXAxisLabel(selectedMetric)
        }
      }
    }
  });
};
const MetricSelector = ({
  allMetricsData,
  metricsToDisplay,
  handleChangeMetric,
  selectedMetric,
  isUngatedForCMPTrellisMigration
}) => {
  const options = useMemo(() => {
    return metricsToDisplay.map(m => ({
      value: m,
      text: I18n.text(allMetricsData[m].labelKey)
    }));
  }, [metricsToDisplay, allMetricsData]);
  if (isUngatedForCMPTrellisMigration) {
    return /*#__PURE__*/_jsx(View, {
      maxInlineSize: "300px",
      children: /*#__PURE__*/_jsx(Select, {
        onSelectedOptionChange: ({
          value
        }) => handleChangeMetric(value),
        options: options,
        selectedValues: [selectedMetric]
      })
    });
  }
  return /*#__PURE__*/_jsx(TransitionalSelect, {
    buttonUse: "transparent",
    onChange: e => handleChangeMetric(e.target.value),
    options: options,
    value: selectedMetric
  });
};
const ChartStyleSelector = ({
  chartStyle,
  handleChangeChartType,
  allMetricsData,
  selectedMetric,
  isUngatedForCMPTrellisMigration
}) => {
  const options = useMemo(() => {
    return getChartStyleOptions(allMetricsData[selectedMetric], isUngatedForCMPTrellisMigration);
  }, [allMetricsData, selectedMetric, isUngatedForCMPTrellisMigration]);
  if (isUngatedForCMPTrellisMigration) {
    return /*#__PURE__*/_jsx(InlineLabel, {
      label: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "content-management-details-lib.reporting.sourcesChart.chartTypeSelectorLabel"
      }),
      minInlineSize: "175px",
      children: /*#__PURE__*/_jsx(Select, {
        onSelectedOptionChange: ({
          value
        }) => handleChangeChartType(value),
        options: options,
        selectedValues: [chartStyle]
      })
    });
  }
  return /*#__PURE__*/_jsx(InlineLabel, {
    label: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "content-management-details-lib.reporting.sourcesChart.chartTypeSelectorLabel"
    }),
    children: /*#__PURE__*/_jsx(TransitionalSelect, {
      buttonUse: "link",
      onChange: e => handleChangeChartType(e.target.value),
      options: options,
      value: chartStyle
    })
  });
};
const SourcesChart = ({
  variationId,
  variationIds
}) => {
  const {
    dateRange,
    d1,
    frequency,
    metric,
    metricsToDisplay,
    allMetricsData,
    checkedSources,
    setMetric,
    source
  } = useReportingFiltersProvider();
  const {
    contentId,
    contentIds
  } = useReportingContentIds({
    variationId,
    variationIds
  });
  const {
    getIsUngatedForCMPTrellisMigration
  } = useAuthInfoProvider();
  const isUngatedForCMPTrellisMigration = getIsUngatedForCMPTrellisMigration();
  const [chartStyle, setChartStyle] = useState(AREA);
  const handleChangeMetric = newSelectedMetric => {
    setMetric(newSelectedMetric);
    if (chartStyle !== LINE) {
      const fullMetric = allMetricsData[newSelectedMetric];
      if (isSecondaryMetric(fullMetric)) {
        setChartStyle(LINE);
      }
    }
  };
  const report = getReport({
    contentId,
    contentIds,
    dateRange,
    d1,
    frequency,
    chartType: chartStyle,
    selectedMetric: metric,
    source,
    checkedSources
  });
  return /*#__PURE__*/_jsxs(CardWrapper, {
    compact: true,
    children: [/*#__PURE__*/_jsx(CardSection, {
      children: /*#__PURE__*/_jsx(FilterBar, {
        flush: true,
        startSlot: /*#__PURE__*/_jsx(MetricSelector, {
          allMetricsData: allMetricsData,
          handleChangeMetric: handleChangeMetric,
          metricsToDisplay: metricsToDisplay,
          selectedMetric: metric,
          isUngatedForCMPTrellisMigration: isUngatedForCMPTrellisMigration
        }),
        endSlot: /*#__PURE__*/_jsx(ChartStyleSelector, {
          chartStyle: chartStyle,
          handleChangeChartType: setChartStyle,
          allMetricsData: allMetricsData,
          selectedMetric: metric,
          isUngatedForCMPTrellisMigration: isUngatedForCMPTrellisMigration
        })
      })
    }), /*#__PURE__*/_jsx(CardSection, {
      children: /*#__PURE__*/_jsx(PlatformReportResolver, {
        report: report,
        useInlineLabels: false
      })
    })]
  });
};
export default SourcesChart;